/* eslint-disable no-magic-numbers */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import runTestMessageBus from "./RunTestEventBus.react";

/**
 * PopTest is a component that tests upload / download speeds to a single POP. Not instantiated directly;
 * see PopTestManager for that.
 * ToDo: All tests to use a mutex lock to ensure only one test runs at a time, otherwise speed would be impacted.
 */
export default class PopTest extends Component {
    constructor(props) {
        super(props);

        this.state = {
            //downloadUrl: "https://download-" + props.popTestName + ".box-test.com/request/download",
            downloadUrl: "https://dl.boxcloud.com/speedtest?size=",
            //uploadUrl: "https://upload-" + props.popTestName + ".box-test.com/request/upload",
            uploadUrl: "https://upload.app.box.com/speedtest",
            uploadSpeed: 0,
            uploadDuration: null,
            uploadRtt: null,
            downloadSpeed: 0,
            downloadDuration: null,
            downloadRtt: null,
            dataSizeMb: 1,
        };

        this.doDownload = this.doDownload.bind(this);
        this.doUpload = this.doUpload.bind(this);
        this.runTests = this.runTests.bind(this);
        this.resetResultsFromState = this.resetResultsFromState.bind(this);
    }

    render() {
        const { id, popDisplayName, } = this.props;
        const {
            downloadSpeed,
            downloadDuration,
            downloadRtt,
            uploadSpeed,
            uploadDuration,
            uploadRtt,
        } = this.state;

        const isDownloadLoading = downloadSpeed === null;
        const isUploadLoading = uploadSpeed === null;

        return (
            <tr id={id}>
                <td><b>{popDisplayName}</b></td>
                <td>
                    {isDownloadLoading
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        : this.state.downloadError
                        ? (this.state.downloadSpeed === "Request timed out" ? "Request timed out" : "Connection Blocked") // Show specific error message
                        : `${this.state.downloadSpeed} Mbps`}
                </td>
                <td>{downloadDuration} ms</td>
                <td>{downloadRtt} ms</td>
                <td>
                    {isUploadLoading
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        : this.state.uploadError
                        ? (this.state.uploadSpeed === "Request timed out" ? "Request timed out" : "Connection Blocked") // Show specific error message
                        : `${this.state.uploadSpeed} Mbps`}
                </td>
                <td>{uploadDuration} ms</td>
                <td>{uploadRtt} ms</td>
            </tr>
        );
    }

    componentDidMount() {
        runTestMessageBus.on("startPopTest", (data) => {
            // console.log("PopDownloader received event with size: " + data.dataSizeMb);
            this.setState({dataSizeMb: data.dataSizeMb}, () => {
                this.runTests()
            })
        });
        if (this.props.autoRun) {
            this.doDownload();
            this.doUpload();
        }
    }

    resetResultsFromState() {
        // reset state values that are used in output
        this.setState({
            uploadSpeed: null,
            uploadDuration: null,
            uploadRtt: null,
            downloadSpeed: null,
            downloadDuration: null,
            downloadRtt: null,
        })
    }

    runTests() {
        const { id } = this.props
        this.resetResultsFromState()
        this.doUpload()
        this.doDownload()
        runTestMessageBus.dispatch("testcomplete", {id:id});
        // Signal end of tests.
    }

    doDownload() {
        // Downloads a file, update state
        const { id } = this.props
        const { downloadUrl, dataSizeMb } = this.state;
        let downloadSizeUrl;
        switch (dataSizeMb) {
            case 1:
                //downloadSizeUrl = "";
                downloadSizeUrl = "1048576";
                break;
            case 10:
                //downloadSizeUrl = "/10M";
                downloadSizeUrl = "10485760";
                break;
            case 100:
                //downloadSizeUrl = "/100M";
                downloadSizeUrl = "104857600";
                break;
            default:
                //downloadSizeUrl = "";
                downloadSizeUrl = "1048576";
        }
        //const url = downloadUrl + downloadSizeUrl + "?ts=" + Date.now();
        const url = downloadUrl + downloadSizeUrl;

        // --- RTT (reachability) timing ---
        const rttStartTime = performance.now();

        // AbortController ONLY for RTT reachability check
        const rttController = new AbortController();
        let rttTimeoutID;

        // 10s timeout ONLY to reach the server (RTT step)
        const timeoutDuration = 10000;
        rttTimeoutID = setTimeout(() => {
          rttController.abort("Download RTT timed out");
          this.setState({
            downloadSpeed: "Request timed out",
            downloadError: true,
          // Optional: surface in RTT column too
          // downloadRtt: "timeout",
          });
        }, timeoutDuration);

        // Step 1: RTT GET (reachability only)
        fetch(url, {
          method: "GET",            // (Use "HEAD" if your server supports it)
          cache: "no-cache",
          signal: rttController.signal, // timeout applies here only
        })
          .then((response) => {
            // Server reachable — clear RTT timeout
            clearTimeout(rttTimeoutID);

            // Measure RTT (to first byte / headers)
            const rttinMillisecondsdownload = (performance.now() - rttStartTime).toFixed(2);
            this.setState({ downloadRtt: rttinMillisecondsdownload });

            // If server responded with an error status, treat as blocked/error before full download
            if (!response.ok) {
              if (response.status >= 500) throw new Error("Server Error");
              throw new Error("Connection Blocked");
            }

            // Prevent downloading the whole RTT body (since we only needed headers)
            // (No-op if response.body is null or already consumed)
            try { response.body?.cancel(); } catch (_) {}

            // Step 2: Actual DOWNLOAD (no RTT timeout tied)
            performance.mark("download-start-" + id);

            // Start a fresh request for the real payload
            return fetch(url, { cache: "no-cache" });
          })
          .then((response) => {
            // Validate response
            if (!response.ok) {
              if (response.status >= 500) throw new Error("Server Error");
              throw new Error("Connection Blocked");
            }
            // Read the full body to measure throughput
            return response.blob();
          })
          .then((blob) => {
            // Completed download: compute duration & speed
            performance.mark("download-end-" + id);
            const downloadMeasure = performance.measure(
              "download-measure" + id,
              "download-start-" + id,
              "download-end-" + id
            );

            const totalDurationSeconds = downloadMeasure.duration / 1000;
            const contentLengthAsBits = blob.size * 8;
            const mbps = (((contentLengthAsBits / totalDurationSeconds) / 1024) / 1024).toFixed(2);

            this.setState({
              downloadSpeed: mbps,
              downloadDuration: Math.round(downloadMeasure.duration),
              downloadError: false,
            });
          })
          .catch((error) => {
            // Ensure RTT timer is cleared in any failure path
            clearTimeout(rttTimeoutID); // <-- fix: was timeoutID
            console.error("Caught error: ", error);

            // If we already marked a timeout, don’t overwrite the message
            if (this.state.downloadSpeed === "Request timed out") return;

            // Timeout on RTT step
            if (error.name === "AbortError") {
              console.error("Download request timed out");
              this.setState({
                  downloadSpeed: "Request timed out",
                  downloadError: true, // Mark as error
              });
              return;
            }

            // Non-timeout errors
            this.setState({
              downloadSpeed: "Connection Blocked",
              downloadError: true,
            });
            console.error("Download error: ", error);
          });
      }


    readableRandomStringMaker(length) {
        let s
        for (s=''; s.length < length; s += 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.charAt(Math.random()*62|0));
        return s;
    }

    doUpload() {
        const { id } = this.props
        const { dataSizeMb, uploadUrl } = this.state
        const url = uploadUrl + "?ts=" + Date.now()
        const bigglyStr = this.readableRandomStringMaker(dataSizeMb*1024*1024)

        // --- RTT (reachability) timing ---
        const rttStartTime = performance.now();

        // AbortController ONLY for RTT GET (reachability)
        const rttController = new AbortController();
        let rttTimeoutID;

        // 10s timeout ONLY to reach the server (RTT step)
        const timeoutDuration = 10000;
        rttTimeoutID = setTimeout(() => {
            rttController.abort("Upload RTT timed out");
            this.setState({
                uploadSpeed: "Request timed out",
                uploadError: true,
            });
        }, timeoutDuration);

        // Step 1: RTT GET (reachability only)
        fetch(url, {
            method: "GET",
            cache: "no-cache",
            signal: rttController.signal, // timeout applies here only
        })
            .then((response) => {
                // Server reachable — clear RTT timeout
                clearTimeout(rttTimeoutID);

                // Measure RTT
                const rttinMillisecondsupload = (performance.now() - rttStartTime).toFixed(2);
                this.setState({ uploadRtt: rttinMillisecondsupload });

                // Step 2: Actual UPLOAD (no timeout tied to the RTT controller)
                performance.mark("upload-start-" + id);

                return fetch(url, {
                    cache: "no-cache",
                    method: "post",
                    headers: {
                        "Content-Type": "application/octet-stream",
                    },
                    body: bigglyStr,
                    // IMPORTANT: no signal here so big uploads aren’t aborted by the RTT timer
                });
            })
            .then((response) => {
                // Upload finished — record end + compute speed
                performance.mark("upload-end-" + id);
                const uploadMeasure = performance.measure(
                    "upload-measure" + id,
                    "upload-start-" + id,
                    "upload-end-" + id
                );

                // Consider non-2xx as errors
                if (!response.ok) {
                    if (response.status >= 500) {
                        throw new Error("Server Error");
                    } else {
                        throw new Error("Connection Blocked");
                    }
                }

                console.log("Upload response status ", response.status);

                // Mbps math (keep your exact math)
                const bigStringSize = new Blob([bigglyStr]).size;
                const totalDurationSeconds = uploadMeasure.duration / 1000;
                const contentLengthAsBits = bigStringSize * 8;
                const mbps = (((contentLengthAsBits / totalDurationSeconds) / 1024) / 1024).toFixed(2);

                this.setState({
                    uploadSpeed: mbps,
                    uploadDuration: Math.round(uploadMeasure.duration),
                    uploadError: false,
                });
            })
            .catch((error) => {
                // Ensure RTT timer is cleared in any failure path
                clearTimeout(rttTimeoutID);

                console.error("Caught error: ", error);

                // If we already marked a timeout, don’t overwrite the message
                if (this.state.uploadSpeed === "Request timed out") {
                    return;
                }

                // Timeout on RTT step
                if (error.name === "AbortError") {
                    console.error("Upload request timed out");
                    this.setState({
                        uploadSpeed: "Request timed out",
                        uploadError: true,
                    });
                    return;
                }

                // Any other failure is treated as connection blocked
                this.setState({
                    uploadSpeed: "Connection Blocked",
                    uploadError: true,
                });
                console.error("Upload error: ", error);
            });
    }
}

PopTest.defaultProps = {
    autoRun: false,
};

PopTest.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string,

    /**
     * The pop name to display in UI
     */
    popDisplayName: PropTypes.string,


    /**
     * The pop to run download tests on
     */
    popTestName: PropTypes.string,

    /**
     * Should upload / download tests run immediately on load?
     */
    autoRun: PropTypes.bool,
};
